import React, { useMemo, useState } from "react";
import moment from "moment";

import { AppLayout } from "../../../components/app-layout/AppLayout";
import { Modal } from "../../../components/ui/Modal";
import { companySelector } from "../../../reducers/sessionReducer";
import { useShallowEqualSelector } from "../../../hooks/useShallowEqualSelector";
import { useEventsCalendarQuery } from "../../../queries/events/useEventsCalendarQuery";
import { useEventDetailQuery } from "../../../queries/events/useEventDetailQuery";
import { useEventRegistrationsQuery } from "../../../queries/events/useEventRegistrationsQuery";
import { useEventRegistrationMutation } from "../../../queries/events/useEventRegistrationMutation";
import { getCompanyStartOfDay, getCompanyEndOfDay } from "../../../helpers/DateUtils";

// --- Types ---

interface PublicEventType {
  id: number;
  name: string;
}

interface PublicLocationType {
  id: number;
  name: string;
}

interface PublicEventStatus {
  id: number;
  name: string;
  code?: string;
}

interface PublicEventClinic {
  id: number;
  name: string;
  address?: string;
  phone?: string;
}

interface AssignedClinician {
  id: number;
  name: string;
  isPrimary: boolean;
  role: string;
}

interface RegistrationPatient {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  mobilePhoneNumber?: string;
}

interface RegistrationType {
  id: number;
  name: string;
  code: string;
}

interface EventRegistration {
  id: number;
  eventId: number;
  registrationDate: number;
  attended: boolean;
  paymentCompleted: boolean;
  paidAmount?: number;
  paymentTransactionId?: string;
  notes?: string;
  registrationGroupId?: number;
  isPrimaryAttendee: boolean;
  relationshipToPrimary?: string | null;
  patient: RegistrationPatient;
  tempPatientId?: number;
  registrationType: RegistrationType;
}

interface PublicEventDetail extends PublicEvent {
  requirements?: string;
  notes?: string;
  zoomLink?: string | null;
  availableSpots?: number;
  clinic: PublicEventClinic;
  assignedClinicians?: AssignedClinician[];
}

interface PublicEvent {
  id: number;
  eventName: string;
  title: string;
  description: string;
  eventDate: number;
  startTime: number;
  endTime: number;
  location: string;
  headerImageUrl?: string;
  eventType: PublicEventType;
  locationType: PublicLocationType;
  status: PublicEventStatus;
  clinic: PublicEventClinic;
  totalCapacity: number;
  currentRegistrations: number;
  requiresPayment: boolean;
  fee?: number | null;
  requirements?: string;
  notes?: string;
  zoomLink?: string | null;
  availableSpots?: number;
}

interface DayCell {
  date: Date;
  isCurrentMonth: boolean;
  events: PublicEvent[];
}

interface RegistrationForm {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  note: string;
}

// --- Constants ---

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EVENTS_STATUSES: { value: string | undefined; label: string }[] = [
  { value: undefined, label: "All" },
  { value: "EVENT_STATUS_PLANNED", label: "Upcoming" },
  { value: "EVENT_STATUS_STARTED", label: "Started" },
  { value: "EVENT_STATUS_IN_PROGRESS", label: "In Progress" },
  { value: "EVENT_STATUS_ENDED", label: "Ended" },
  { value: "EVENT_STATUS_CANCELLED", label: "Cancelled" },
];

const initialFormState: RegistrationForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  note: "",
};

// --- Helpers ---

const getStatusColor = (event: PublicEvent) => {
  const statusName = event.status?.name?.toLowerCase() || "";
  if (statusName.includes("started")) return "#4caf50";
  if (statusName.includes("progress")) return "#ff9800";
  if (statusName.includes("ended")) return "#9e9e9e";
  if (statusName.includes("cancelled")) return "#f44336";
  return "#2196f3";
};

const getAvailableSpots = (event: PublicEvent) => event.totalCapacity - event.currentRegistrations;

const formatEventTime = (event: PublicEvent) => {
  const startHours = Math.floor(event.startTime / (60 * 60 * 1000));
  const startMinutes = Math.floor((event.startTime % (60 * 60 * 1000)) / (60 * 1000));
  const endHours = Math.floor(event.endTime / (60 * 60 * 1000));
  const endMinutes = Math.floor((event.endTime % (60 * 60 * 1000)) / (60 * 1000));
  const formatTime = (h: number, m: number) => {
    const period = h >= 12 ? "PM" : "AM";
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
  };
  return `${formatTime(startHours, startMinutes)} - ${formatTime(endHours, endMinutes)}`;
};

// --- Event Modal (Details + Registration in a single Modal) ---

interface EventModalProps {
  event: PublicEvent;
  companyId: number;
  onClose: () => void;
  registrationSuccess: string | null;
  onRegistrationSuccess: (message: string) => void;
}

const EventModal: React.FC<EventModalProps> = ({
  event,
  companyId,
  onClose,
  registrationSuccess,
  onRegistrationSuccess,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<RegistrationForm>(initialFormState);
  const [error, setError] = useState<string | null>(null);
  const registrationMutation = useEventRegistrationMutation();

  const [showParticipants, setShowParticipants] = useState(false);

  const { data: detailData, isLoading: isDetailLoading } = useEventDetailQuery({
    id: event.id,
    companyId,
  });

  const { data: registrationsData, isLoading: isRegistrationsLoading } = useEventRegistrationsQuery(
    {
      eventId: event.id,
      companyId,
    },
  );

  const eventDetail: PublicEventDetail | null = useMemo(
    () => detailData?.data || detailData || null,
    [detailData],
  );

  const registrations: EventRegistration[] = useMemo(
    () => registrationsData?.data || registrationsData || [],
    [registrationsData],
  );

  const displayEvent = eventDetail || event;

  const handleFormChange = (field: keyof RegistrationForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!form.firstName.trim()) return "First name is required";
    if (!form.lastName.trim()) return "Last name is required";
    if (!form.phone.trim() && !form.email.trim()) return "Either phone or email is required";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return "Please enter a valid email address";
    }
    return null;
  };

  const handleSubmit = () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    registrationMutation.mutate(
      {
        eventId: event.id,
        companyId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        note: form.note.trim() || undefined,
      },
      {
        onSuccess: (data: any) => {
          if (data.statusCode === 2 && data.data?.success) {
            onRegistrationSuccess(data.message || "You have been successfully registered!");
            setShowForm(false);
          } else {
            setError(data.message || "Registration failed. Please try again.");
          }
        },
        onError: () => setError("An error occurred. Please try again."),
      },
    );
  };

  const spots = eventDetail?.availableSpots ?? getAvailableSpots(event);

  return (
    <Modal show={true} onHide={onClose} title={event.eventName}>
      {!showForm && !showParticipants ? (
        <>
          {displayEvent.headerImageUrl && (
            <div className="events-header-image">
              <img src={displayEvent.headerImageUrl} alt={displayEvent.eventName} />
            </div>
          )}
          <div className="events-modal-body">
            {isDetailLoading && (
              <div className="events-detail-loading">Loading event details...</div>
            )}
            {registrationSuccess && (
              <div className="events-success-message">{registrationSuccess}</div>
            )}
            <div className="events-detail-grid">
              <div className="events-detail">
                <span className="events-detail-label">Date</span>
                <span className="events-detail-value">
                  {moment(displayEvent.eventDate).format("dddd, MMMM D, YYYY")}
                </span>
              </div>
              <div className="events-detail">
                <span className="events-detail-label">Time</span>
                <span className="events-detail-value">{formatEventTime(displayEvent)}</span>
              </div>
              <div className="events-detail">
                <span className="events-detail-label">Status</span>
                <span
                  className="events-detail-value"
                  style={{ color: getStatusColor(displayEvent) }}
                >
                  {displayEvent.status?.name || "Planned"}
                </span>
              </div>
              <div className="events-detail">
                <span className="events-detail-label">Type</span>
                <span className="events-detail-value">
                  {displayEvent.eventType?.name || "General"} (
                  {displayEvent.locationType?.name || "On-site"})
                </span>
              </div>
              <div className="events-detail">
                <span className="events-detail-label">Location</span>
                <span className="events-detail-value">
                  {displayEvent.location || displayEvent.clinic?.name || "TBA"}
                </span>
              </div>
            </div>
            {displayEvent.description && (
              <div className="events-detail">
                <span className="events-detail-label">Description</span>
                <span className="events-detail-value">{displayEvent.description}</span>
              </div>
            )}
            {eventDetail?.requirements && (
              <div className="events-detail">
                <span className="events-detail-label">Requirements</span>
                <span className="events-detail-value">{eventDetail.requirements}</span>
              </div>
            )}
            {eventDetail?.notes && (
              <div className="events-detail">
                <span className="events-detail-label">Notes</span>
                <span className="events-detail-value">{eventDetail.notes}</span>
              </div>
            )}
            {eventDetail?.zoomLink && (
              <div className="events-detail">
                <span className="events-detail-label">Zoom Link</span>
                <span className="events-detail-value">
                  <a
                    href={eventDetail.zoomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="events-detail-link"
                  >
                    {eventDetail.zoomLink}
                  </a>
                </span>
              </div>
            )}
            {eventDetail?.clinic?.address && (
              <div className="events-detail-section">
                <span className="events-detail-section-title">Clinic Information</span>
                <div className="events-detail">
                  <span className="events-detail-label">Clinic</span>
                  <span className="events-detail-value">{eventDetail.clinic.name}</span>
                </div>
                <div className="events-detail">
                  <span className="events-detail-label">Address</span>
                  <span className="events-detail-value">{eventDetail.clinic.address}</span>
                </div>
                {eventDetail.clinic.phone && (
                  <div className="events-detail">
                    <span className="events-detail-label">Phone</span>
                    <span className="events-detail-value">{eventDetail.clinic.phone}</span>
                  </div>
                )}
              </div>
            )}
            {eventDetail?.assignedClinicians && eventDetail.assignedClinicians.length > 0 && (
              <div className="events-detail-section">
                <span className="events-detail-section-title">Clinicians</span>
                <div className="events-clinicians-list">
                  {eventDetail.assignedClinicians.map((clinician) => (
                    <div key={clinician.id} className="events-clinician-item">
                      <span className="events-clinician-name">{clinician.name}</span>
                      <span className="events-clinician-role">
                        {clinician.role}
                        {clinician.isPrimary && (
                          <span className="events-clinician-primary">Primary</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="events-capacity">
              <div className="events-capacity-info">
                <span className="events-capacity-label">Available Spots</span>
                <span className="events-capacity-value">
                  {spots} / {displayEvent.totalCapacity}
                </span>
              </div>
              <div className="events-capacity-bar">
                <div
                  className="events-capacity-fill"
                  style={{
                    width: `${
                      ((displayEvent.totalCapacity - spots) / displayEvent.totalCapacity) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
            {displayEvent.requiresPayment && displayEvent.fee && (
              <div className="events-detail">
                <span className="events-detail-label">Fee</span>
                <span className="events-detail-value">${displayEvent.fee.toFixed(2)}</span>
              </div>
            )}
          </div>
          <div className="events-modal-footer">
            <button type="button" className="events-modal-button" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="events-modal-button"
              onClick={() => setShowParticipants(true)}
            >
              Participants ({registrations.length})
            </button>
            {!registrationSuccess && (
              <button
                type="button"
                className="events-modal-button primary"
                onClick={() => setShowForm(true)}
                disabled={spots <= 0}
              >
                {spots <= 0 ? "Fully Booked" : "Book Seat"}
              </button>
            )}
          </div>
        </>
      ) : showParticipants ? (
        <>
          <div className="events-modal-body">
            <div className="events-participants-header">
              <span className="events-detail-section-title">
                Registered Participants ({registrations.length})
              </span>
            </div>
            {isRegistrationsLoading && (
              <div className="events-detail-loading">Loading participants...</div>
            )}
            {!isRegistrationsLoading && registrations.length === 0 && (
              <div className="events-participants-empty">No participants registered yet.</div>
            )}
            {registrations.length > 0 && (
              <div className="events-participants-list">
                {registrations.map((reg) => (
                  <div key={reg.id} className="events-participant-card">
                    <div className="events-participant-info">
                      <span className="events-participant-name">
                        {reg.patient.firstName} {reg.patient.lastName}
                      </span>
                      <div className="events-participant-badges">
                        {reg.isPrimaryAttendee && (
                          <span className="events-clinician-primary">Primary</span>
                        )}
                        {reg.attended && (
                          <span className="events-participant-badge attended">Attended</span>
                        )}
                        {reg.paymentCompleted && (
                          <span className="events-participant-badge paid">Paid</span>
                        )}
                      </div>
                    </div>
                    <div className="events-participant-details">
                      {reg.patient.email && (
                        <span className="events-participant-contact">{reg.patient.email}</span>
                      )}
                      {reg.patient.mobilePhoneNumber && (
                        <span className="events-participant-contact">
                          {reg.patient.mobilePhoneNumber}
                        </span>
                      )}
                    </div>
                    <div className="events-participant-meta">
                      <span className="events-participant-date">
                        Registered: {moment(reg.registrationDate).format("MMM D, YYYY")}
                      </span>
                      <span className="events-participant-type">{reg.registrationType?.name}</span>
                      {reg.relationshipToPrimary && (
                        <span className="events-participant-relationship">
                          ({reg.relationshipToPrimary})
                        </span>
                      )}
                    </div>
                    {reg.notes && <div className="events-participant-notes">{reg.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="events-modal-footer">
            <button
              type="button"
              className="events-modal-button"
              onClick={() => setShowParticipants(false)}
            >
              Back
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="events-modal-body">
            <p className="events-form-description">
              Please fill in your details to register for this event.
            </p>
            {error && <div className="events-error-message">{error}</div>}
            <div className="events-form-group">
              <label className="events-form-label">First Name *</label>
              <input
                className="events-form-input"
                value={form.firstName}
                onChange={(e) => handleFormChange("firstName", e.target.value)}
                placeholder="Enter your first name"
              />
            </div>
            <div className="events-form-group">
              <label className="events-form-label">Last Name *</label>
              <input
                className="events-form-input"
                value={form.lastName}
                onChange={(e) => handleFormChange("lastName", e.target.value)}
                placeholder="Enter your last name"
              />
            </div>
            <div className="events-form-group">
              <label className="events-form-label">Phone</label>
              <input
                className="events-form-input"
                value={form.phone}
                onChange={(e) => handleFormChange("phone", e.target.value)}
                placeholder="Enter your phone number"
              />
            </div>
            <div className="events-form-group">
              <label className="events-form-label">Email</label>
              <input
                className="events-form-input"
                value={form.email}
                onChange={(e) => handleFormChange("email", e.target.value)}
                placeholder="Enter your email address"
                type="email"
              />
            </div>
            <div className="events-form-group">
              <label className="events-form-label">Note (optional)</label>
              <input
                className="events-form-input"
                value={form.note}
                onChange={(e) => handleFormChange("note", e.target.value)}
                placeholder="Any additional information"
              />
            </div>
            <p className="events-form-hint">* Either phone or email is required</p>
          </div>
          <div className="events-modal-footer">
            <button
              type="button"
              className="events-modal-button"
              onClick={() => setShowForm(false)}
            >
              Back
            </button>
            <button
              type="button"
              className="events-modal-button primary"
              onClick={handleSubmit}
              disabled={registrationMutation.isLoading}
            >
              {registrationMutation.isLoading ? "Registering..." : "Register"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

// --- Main Events Page ---

export const EventsPage: React.FC = () => {
  // @ts-ignore
  const company = useShallowEqualSelector(companySelector);
  const companyId = company?.id || 1;

  const [month, setMonth] = useState(moment().month());
  const [year, setYear] = useState(moment().year());
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [selectedEvent, setSelectedEvent] = useState<PublicEvent | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState<string | null>(null);

  const monthsOptions = moment.months();

  const yearsOptions = useMemo(
    () => Array.from({ length: 10 }, (_, i) => moment().year() - 5 + i),
    [],
  );

  const calendarRange = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    return {
      start: getCompanyStartOfDay(firstDayOfMonth),
      end: getCompanyEndOfDay(lastDayOfMonth),
    };
  }, [year, month]);

  const { data: eventsData, isLoading } = useEventsCalendarQuery({
    companyId,
    startDate: calendarRange.start,
    endDate: calendarRange.end,
    statusCode: status,
    limit: 50,
  });

  const events: PublicEvent[] = useMemo(() => eventsData?.data?.list || eventsData?.list || [], [
    eventsData,
  ]);

  const calendarDays: DayCell[] = useMemo(() => {
    const days: DayCell[] = [];
    const firstDayOfMonth = new Date(year, month, 1);
    const currentDate = new Date(firstDayOfMonth);
    currentDate.setDate(currentDate.getDate() - currentDate.getDay());

    for (let i = 0; i < 42; i++) {
      const date = new Date(currentDate);
      const isCurrentMonth = date.getMonth() === month && date.getFullYear() === year;
      const dayStart = moment(date).startOf("day").valueOf();
      const dayEnd = moment(date).endOf("day").valueOf();
      const dayEvents = events.filter((e) => e.eventDate >= dayStart && e.eventDate <= dayEnd);
      days.push({ date, isCurrentMonth, events: dayEvents });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  }, [year, month, events]);

  const handleDateChange = (newYear: number, newMonth: number) => {
    setYear(newYear);
    setMonth(newMonth);
  };

  const handleArrowDateChange = (action: "next" | "previous") => {
    const minYear = moment().year() - 5;
    const maxYear = minYear + 10;
    if (action === "next") {
      if (month < 11) return handleDateChange(year, month + 1);
      if (year + 1 < maxYear) return handleDateChange(year + 1, 0);
    }
    if (action === "previous") {
      if (month > 0) return handleDateChange(year, month - 1);
      if (year - 1 >= minYear) return handleDateChange(year - 1, 11);
    }
  };

  const handleEventClick = (event: PublicEvent) => {
    setSelectedEvent(event);
    setRegistrationSuccess(null);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setRegistrationSuccess(null);
  };

  const handleStatusFilter = (statusCode: string | undefined) => {
    setStatus(statusCode);
  };

  const handleRegistrationSuccess = (message: string) => {
    setRegistrationSuccess(message);
  };

  const handleToday = () => {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  const isToday = (date: Date) => date.toDateString() === new Date().toDateString();

  return (
    <AppLayout title="Events Calendar" scrollable={true}>
      <div className="events-page">
        {/* Header: navigation + today button */}
        <div className="events-header">
          <div className="events-navigation">
            <button
              type="button"
              className="events-nav-button"
              onClick={() => handleArrowDateChange("previous")}
            >
              &#8249;
            </button>
            <div className="events-selectors">
              <select
                className="events-select"
                value={month}
                onChange={(e) => handleDateChange(year, Number(e.target.value))}
              >
                {monthsOptions.map((name, index) => (
                  <option key={index} value={index}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                className="events-select"
                value={year}
                onChange={(e) => handleDateChange(Number(e.target.value), month)}
              >
                {yearsOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="events-nav-button"
              onClick={() => handleArrowDateChange("next")}
            >
              &#8250;
            </button>
          </div>
          <div className="events-header-right">
            {isLoading && <span className="events-loading">Loading...</span>}
            <button type="button" className="events-today-button" onClick={handleToday}>
              Today
            </button>
          </div>
        </div>

        {/* Status filter bar */}
        <div className="events-status-filter">
          {EVENTS_STATUSES.map((s) => (
            <button
              key={s.label}
              type="button"
              className={`events-status-button ${
                status === s.value || (!status && !s.value) ? "active" : ""
              }`}
              onClick={() => handleStatusFilter(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Monthly calendar grid */}
        <div className="events-calendar">
          <div className="events-week-header">
            {WEEK_DAYS.map((day) => (
              <div key={day} className="events-week-day">
                {day}
              </div>
            ))}
          </div>
          <div className="events-days-grid">
            {calendarDays.map((day, index) => (
              <div
                key={index}
                className={`events-day-cell${!day.isCurrentMonth ? " other-month" : ""}${
                  isToday(day.date) ? " today" : ""
                }`}
              >
                <div className="events-day-number">{day.date.getDate()}</div>
                <div className="events-container">
                  {day.events.slice(0, 3).map((event) => (
                    <button
                      key={event.id}
                      className="events-event-item"
                      style={{ backgroundColor: getStatusColor(event) }}
                      onClick={() => handleEventClick(event)}
                      title={`${event.eventName} - ${event.status?.name || "Planned"}`}
                    >
                      <span className="events-event-time">
                        {moment(event.eventDate).format("HH:mm")}
                      </span>
                      <span className="events-event-name">{event.eventName}</span>
                    </button>
                  ))}
                  {day.events.length > 3 && (
                    <div className="events-more-events">+{day.events.length - 3} more</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Event Modal (Details + Registration) */}
        {selectedEvent && (
          <EventModal
            event={selectedEvent}
            companyId={companyId}
            onClose={handleCloseModal}
            registrationSuccess={registrationSuccess}
            onRegistrationSuccess={handleRegistrationSuccess}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default EventsPage;
